import { Component, OnInit, signal } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router, RouterModule, ActivatedRoute } from '@angular/router';
import { CustomerCardService, ArkifiHubCustomerCardDto } from '../../services/customer-card.service';
import { ArkifiHubPlatformService } from '../../services/arkifihub-platform.service';
import { AuthService } from '../../services/auth.service';
import { SettingsService } from '../../services/settings.service';
import { MessageService } from 'primeng/api';
import { ToastModule } from 'primeng/toast';
import { ProgressSpinnerModule } from 'primeng/progressspinner';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { ConfirmationService } from 'primeng/api';

@Component({
  selector: 'app-cards',
  standalone: true,
  imports: [CommonModule, RouterModule, ToastModule, ProgressSpinnerModule, ConfirmDialogModule],
  providers: [MessageService, ConfirmationService],
  templateUrl: './cards.html',
  styleUrl: './cards.css'
})
export class CardsComponent implements OnInit {
  isLoading = signal(true);
  cards = signal<ArkifiHubCustomerCardDto[]>([]);
  error = signal<string | null>(null);
  platformId = signal<string | null>(null);
  platformName = signal<string | null>(null);
  environment = signal<'Test' | 'Live' | null>(null);
  supportsSavedCards = signal<boolean | null>(null);

  constructor(
    private router: Router,
    private route: ActivatedRoute,
    private cardService: CustomerCardService,
    private arkifiHubPlatformService: ArkifiHubPlatformService,
    public authService: AuthService,
    private settingsService: SettingsService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService
  ) {}

  ngOnInit(): void {
    if (!this.authService.isAuthenticated()) {
      this.router.navigate(['/signin']);
      return;
    }

    // Get environment from SettingsService
    this.environment.set(this.settingsService.getEnvironment());

    // Get platformId from route or query params
    this.route.params.subscribe(params => {
      if (params['id']) {
        this.platformId.set(params['id']);
        this.loadSupportsSavedCards();
        this.loadCards();
      }
    });

    this.route.queryParams.subscribe(params => {
      if (params['platformId']) {
        this.platformId.set(params['platformId']);
      }
      
      // Get platform name from localStorage (stored when navigating from store)
      const storedPlatformName = localStorage.getItem('arkifihub_platformName');
      if (storedPlatformName) {
        this.platformName.set(storedPlatformName);
      }
      
      // Only load cards if we have platformId
      if (this.platformId()) {
        this.loadSupportsSavedCards();
        this.loadCards();
      } else {
        this.isLoading.set(false);
        this.error.set('Platform ID is required. Please navigate from a store page.');
      }
    });
  }

  loadSupportsSavedCards(): void {
    const platformId = this.platformId();
    if (!platformId) return;
    const env = this.settingsService.getEnvironment();
    this.arkifiHubPlatformService.getSupportsSavedCards(platformId, env).subscribe({
      next: (res) => {
        this.supportsSavedCards.set(res.data?.supportsSavedCards ?? false);
      },
      error: () => {
        this.supportsSavedCards.set(false);
      }
    });
  }

  async loadCards(): Promise<void> {
    const platformId = this.platformId();
    if (!platformId) {
      this.isLoading.set(false);
      this.error.set('Platform ID is required');
      return;
    }

    // Check authentication
    if (!this.authService.isAuthenticated()) {
      this.isLoading.set(false);
      this.error.set('You must be signed in to view your cards');
      this.messageService.add({
        severity: 'warn',
        summary: 'Authentication Required',
        detail: 'Please sign in to view your cards',
        life: 5000
      });
      setTimeout(() => {
        this.router.navigate(['/signin']);
      }, 2000);
      return;
    }

    this.isLoading.set(true);
    this.error.set(null);

    try {
      const result = await this.cardService.getCustomerCards(
        platformId,
        this.environment() || undefined
      );

      this.isLoading.set(false);

      if (result.success && result.data) {
        this.cards.set(result.data);
      } else {
        const errorMessage = result.message || 'Failed to load cards';
        this.error.set(errorMessage);
        
        // If session expired, redirect to sign in
        if (errorMessage.includes('session has expired') || errorMessage.includes('must be signed in')) {
          this.messageService.add({
            severity: 'warn',
            summary: 'Session Expired',
            detail: 'Please sign in again to continue',
            life: 5000
          });
          setTimeout(() => {
            this.router.navigate(['/signin']);
          }, 2000);
        } else {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: errorMessage,
            life: 5000
          });
        }
      }
    } catch (error: any) {
      this.isLoading.set(false);
      const errorMessage = error.error?.message || 'An error occurred while loading cards';
      this.error.set(errorMessage);
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: errorMessage,
        life: 5000
      });
      console.error('Error loading cards:', error);
    }
  }

  async deactivateCard(card: ArkifiHubCustomerCardDto): Promise<void> {
    this.confirmationService.confirm({
      message: `Are you sure you want to deactivate the card ending in ${card.last4}? This action cannot be undone.`,
      header: 'Confirm Deactivation',
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: 'Yes, Deactivate',
      rejectLabel: 'Cancel',
      acceptButtonStyleClass: 'p-button-danger p-button-outlined',
      rejectButtonStyleClass: 'p-button-secondary p-button-outlined',
      acceptIcon: 'pi pi-trash',
      rejectIcon: 'pi pi-times',
      accept: async () => {
        try {
          const result = await this.cardService.deactivateCard(card.id);
          if (result.success) {
            this.messageService.add({
              severity: 'success',
              summary: 'Success',
              detail: 'Card deactivated successfully',
              life: 5000
            });
            await this.loadCards();
          } else {
            this.messageService.add({
              severity: 'error',
              summary: 'Error',
              detail: result.message || 'Failed to deactivate card',
              life: 5000
            });
          }
        } catch (error: any) {
          this.messageService.add({
            severity: 'error',
            summary: 'Error',
            detail: error.message || 'An error occurred',
            life: 5000
          });
        }
      }
    });
  }

  goBack(): void {
    const platformId = this.platformId();
    const platformName = this.platformName();
    
    // Navigate back to store page if we have both platformId and platformName
    if (platformId && platformName) {
      const encodedName = encodeURIComponent(platformName);
      this.router.navigate(['/store', platformId, encodedName]);
    } else {
      // Fallback to marketplace if we don't have store info
      this.router.navigate(['/marketplace']);
    }
  }

  goToAddCard(): void {
    const platformId = this.platformId();
    
    if (!platformId) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Platform ID is required to add a card',
        life: 5000
      });
      return;
    }
    
    // Don't pass environment - it will be read from SettingsService
    this.router.navigate(['/add-card'], {
      queryParams: {
        platformId: platformId
      }
    });
  }

  formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch {
      return 'N/A';
    }
  }

  getCardTypeDisplay(cardType?: string): string {
    if (!cardType) return 'Card';
    return cardType.charAt(0).toUpperCase() + cardType.slice(1).toLowerCase();
  }

  getEnvironmentDisplay(env: number): string {
    return env === 2 ? 'Live' : 'Test';
  }

  getMaskedCardNumber(last4?: string): string {
    if (!last4) return '**** **** **** ****';
    return `**** **** **** ${last4}`;
  }
}
